import * as grpcWeb from 'grpc-web'
import { ConsoleServiceClient } from '@/generated/ConsoleServiceClientPb'
import { authMode, clearSession, getSessionCsrf, getStoredApiKey } from '@/lib/auth/session'

// gRPC-Web client factory for ConsoleService.
//
// Transport contract (PR #1245, services/console/server/grpcweb.go):
// binary grpc-web (application/grpc-web+proto) against
// <origin>/atfai.console.v1.ConsoleService/<Method>, with
// `Authorization: Bearer <operator key>` passed through untouched as gRPC
// metadata.
//
// Phase 5C (ADR-118 console UI deploy): charts/atf-console's Ingress now
// path-splits a SINGLE host between this static UI (path `/`) and the
// ConsoleService API (path `/atfai.console.v1.ConsoleService/`, port 8130)
// -- see charts/atf-console/templates/ingress.yaml. Same-origin routing
// means the browser client needs NO absolute target host at all: an empty
// hostname makes grpc-web's generated client resolve every RPC path
// relative to the page's own origin, so this works unmodified behind
// ANY hostname (console.atf.sparbz.cloud today, a future IBM/other overlay
// host tomorrow) with zero rebuild and zero CORS exposure -- the
// server-side CONSOLE_UI_ALLOWED_ORIGINS allowlist (server/grpcweb.go) can
// stay empty/fail-closed in every environment, since no cross-origin call
// is ever made.
//
// VITE_CONSOLE_GRPC_WEB_URL remains a supported ESCAPE HATCH for local dev
// against a standalone/port-forwarded services/console instance that is
// NOT fronted by the same Ingress as this UI (e.g. `npm run dev` on :3005
// talking to a separately port-forwarded services/console on :8130) --
// set it in a local .env file (see vite.config.ts's loadEnv call; Vite
// reads .env/.env.local automatically, no committed example file exists
// today). It is never baked into the production image; the Dockerfile's
// ARG default is intentionally the empty string (same-origin).
const GRPC_WEB_URL = import.meta.env.VITE_CONSOLE_GRPC_WEB_URL || ''

let client: ConsoleServiceClient | null = null

function getClient(): ConsoleServiceClient {
  if (!client) {
    // withCredentials: true makes grpc-web set XMLHttpRequest.withCredentials,
    // so the browser attaches the HttpOnly `console_session` cookie on every
    // gRPC-Web call — this is what carries a PASSWORD/OIDC (cookie-mode)
    // session (ADR-118/ADR-119 Phase 7). grpc-web's GrpcWebClientBase supports
    // this natively (GrpcWebClientBaseOptions.withCredentials); no fetch
    // transport shim is needed. It is a client-level (not per-call) option, so
    // it is set unconditionally: it is a no-op in API-key mode (there is no
    // console_session cookie to send, and same-origin requests carry no
    // ambient cookies the server would misread — the interceptor authenticates
    // Bearer FIRST and only consults the cookie when no Bearer is present).
    // Same-origin (Phase 5C single-host Ingress) means withCredentials adds no
    // CORS surface. The third ctor arg is the GrpcWebClientBaseOptions bag.
    client = new ConsoleServiceClient(GRPC_WEB_URL, null, { withCredentials: true })
  }
  return client
}

// viewerSafeCsrfExemptExceptions mirrors services/console/server/interceptors.go's
// viewerSafeMethodExceptions EXACTLY (camelCased method names). The server
// gates CSRF on cookie-authenticated calls with `if !isViewerSafeMethod(m)`
// — so the client must attach X-CSRF-Token on precisely the set of calls the
// server treats as NOT viewer-safe. That set is: every method NOT named
// Get*/List*, PLUS the documented overrides here. Keeping this table in
// lockstep with the server is load-bearing: attaching CSRF on too few calls
// gets a mutation rejected with PermissionDenied; the server is the authority,
// this is a faithful mirror so the happy path never trips.
//   - whoAmI: NOT Get/List by name, but server marks it viewer-safe (read of
//     own identity) => NO csrf.
//   - listApiKeys: List* by name, but server marks it NOT viewer-safe
//     (admin-only) => REQUIRES csrf in cookie mode, even though it is a read.
const viewerSafeMethodOverrides: Partial<Record<keyof ConsoleServiceClient, boolean>> = {
  whoAmI: true,
  listApiKeys: false,
}

// isCsrfRequiredMethod reports whether a cookie-authenticated call to this
// method must carry the X-CSRF-Token header — i.e. whether the server treats
// it as a mutation (`!isViewerSafeMethod`). Read-only Get*/List* calls skip
// CSRF (a forced read is not a CSRF concern); everything else requires it.
function isCsrfRequiredMethod(methodName: keyof ConsoleServiceClient): boolean {
  const override = viewerSafeMethodOverrides[methodName]
  if (override !== undefined) return !override
  const name = String(methodName)
  const isRead = name.startsWith('get') || name.startsWith('list')
  return !isRead
}

// onUnauthenticated is set once by the auth context so this transport
// layer can react to a 401 from ANY call site without every page needing
// to remember to check for it. See ADR-119: Unauthenticated is returned
// for a missing/malformed header, unknown key prefix, hash mismatch, or a
// revoked/expired/disabled principal -- there is deliberately no
// distinction surfaced to the client between these (fail-closed, no
// timing/enumeration oracle), so the UI's only correct reaction to ANY
// Unauthenticated is "the session is no longer valid, clear it and
// re-prompt for a key."
let onUnauthenticated: (() => void) | null = null

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler
}

/**
 * callConsole invokes a unary ConsoleServiceClient method with the stored
 * operator API key attached as Bearer metadata, and centralizes the
 * 401 -> clear-session-and-redirect reaction (ADR-119 enforcement: any
 * Unauthenticated response, from any RPC, means the credential is no
 * longer good — never just some RPCs).
 *
 * methodName must be the exact ConsoleServiceClient method name (e.g.
 * "listInstances") -- kept as a runtime string dispatch (rather than typed
 * per-call wrappers, which we still provide in console-api.ts) so this one
 * function is the single choke point every call funnels through.
 */
export async function callConsole<Req, Res>(
  methodName: keyof ConsoleServiceClient,
  request: Req,
): Promise<Res> {
  const metadata = buildMetadata(methodName)

  const rpc = getClient()[methodName] as unknown as (
    req: Req,
    md: grpcWeb.Metadata,
  ) => Promise<Res>

  try {
    return await rpc.call(getClient(), request, metadata)
  } catch (err) {
    const rpcErr = err as grpcWeb.RpcError
    if (rpcErr && rpcErr.code === grpcWeb.StatusCode.UNAUTHENTICATED) {
      clearSession()
      onUnauthenticated?.()
    }
    throw err
  }
}

// buildMetadata assembles the per-call gRPC metadata for the CURRENT auth mode:
//
//   - key mode:   Authorization: Bearer <operator key>. No CSRF (a bearer
//                 credential is not ambient — it cannot be forged cross-site —
//                 so the double-submit check does not apply; the server
//                 exempts API-key callers from CSRF entirely).
//   - cookie mode: NO Authorization header (the HttpOnly session cookie rides
//                 automatically via withCredentials). X-CSRF-Token is attached
//                 ONLY on mutating calls (isCsrfRequiredMethod), matching the
//                 server's `if !isViewerSafeMethod` CSRF gate — reads carry no
//                 CSRF header, exactly as the server permits.
//   - none:       empty metadata (an unauthenticated probe; the server will
//                 reject anything requiring auth).
//
// Mode is derived fresh on every call from session state (authMode()), so a
// login/logout that flips the mode is picked up immediately with no client
// rebuild — the singleton transport (withCredentials) is mode-agnostic; only
// the per-call headers differ.
function buildMetadata(methodName: keyof ConsoleServiceClient): grpcWeb.Metadata {
  const mode = authMode()
  if (mode === 'key') {
    const key = getStoredApiKey()
    return key ? { Authorization: `Bearer ${key}` } : {}
  }
  if (mode === 'cookie' && isCsrfRequiredMethod(methodName)) {
    const csrf = getSessionCsrf()
    if (csrf) return { 'X-CSRF-Token': csrf }
  }
  return {}
}

export { getClient as getConsoleClient, buildMetadata as __buildMetadata }
